import { TFunction } from "i18next";
import * as Yup from "yup";

import { nullableAmount, nullableText } from "../../utils/validation.utils";
import { IncomeEntryFormValue, IncomeMaxLength, IncomeTypeTraits } from "./IncomeView.types";
import { calculateAverage, isMonthRecorded } from "./IncomeView.utils";

const TAB = "clients.client.debtSettlement.assetInventory.incomeTab";
const SECTION = `${TAB}.validation`;

export function createSelfEmploymentSchema(t: TFunction) {
  return {
    selfEmploymentDescription: nullableText(t, IncomeMaxLength.selfEmploymentDescription),
    selfEmploymentMonthlyIncome: nullableAmount(t),
  };
}

export function createIncomeEntrySchema(t: TFunction, traitsOf: (incomeType: string | null) => IncomeTypeTraits) {
  return Yup.lazy((entry: IncomeEntryFormValue) => {
    const traits = traitsOf(entry.incomeType);

    return Yup.object().shape({
      payerNameAndAddress: nullableText(t, IncomeMaxLength.payerNameAndAddress),
      dailyRate: nullableAmount(t),
      note: nullableText(t, IncomeMaxLength.note),
      benefitInKindAmount: nullableAmount(t),
      monthlyTipAmount: nullableAmount(t),
      isAverageRepresentative: Yup.boolean()
        .nullable()
        .test(
          "average-determinable",
          t(`${SECTION}.checkIncome`),
          (isRepresentative) =>
            isRepresentative !== true ||
            !entry.months.every(isMonthRecorded) ||
            (calculateAverage(entry, traits.isNonSeizable).amount ?? 0) > 0
        ),
      nonRepresentativeReason: nullableText(t, IncomeMaxLength.nonRepresentativeReason),
      actualAverageIncome: nullableAmount(t),
      months: Yup.array()
        .of(
          Yup.object().shape({
            month: Yup.string().nullable(),
            paidAmount: nullableAmount(t),
            garnishedAmount: nullableAmount(t),
            expenseAllowance: nullableAmount(t),
            familyAllowance: nullableAmount(t),
            employerSettledExpenses: nullableAmount(t),
          })
        )
        .test("distinct-months", t(`${SECTION}.duplicateMonth`), (months) => {
          const selected = (months ?? []).map((m) => m.month).filter((key) => !!key);
          return new Set(selected).size === selected.length;
        }),
    });
  });
}
